import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  // Firestore,
  orderBy,
  FieldPath,
  WhereFilterOp,
  // QuerySnapshot,
  DocumentData,
  limit,
  QueryDocumentSnapshot,
  // onSnapshot,
  // Unsubscribe,
  DocumentSnapshot,
  setDoc,
  deleteDoc,
  // Query,
  // DocumentReference,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@app/configs/firebase";
import {
  DELAY_BOOKING_OPTION,
  ECollectionName,
  EStatusSearchPrice,
} from "../../types";

export type SnapPayload = {
  path: string;
  query?: {
    where?: Array<
      [fieldPath: string | FieldPath, opStr: WhereFilterOp, value: unknown]
    >;
    orderBy?: string;
    limit?: number;
  };
};

type SetPayload = {
  type: "set";
  path: string;
  data: Record<string, unknown>;
};
type CallPayload = {
  type: "call";
  path: string;
  function: string;
  data: Record<string, unknown>;
};
type RemovePayload = {
  type: "remove";
  path: string;
};

type ChangePayload = {
  type: "change";
  path: string;
  data: Record<string, unknown>;
};

type Snap = {
  id: DocumentSnapshot<DocumentData>["id"];
} & DocumentData;

type SnapData = Record<Snap["id"], Snap>;
type SnapCollection = Record<string, SnapData>;
const getSnapCollection = async (
  payload: SnapPayload,
  onEach?: (
    doc: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>
  ) => void
): Promise<SnapCollection> => {
  const snap: SnapData = {};
  const pathArr = payload.path.split(".");
  const [path, ...pathSegments] = pathArr;
  const isCollection = Boolean(pathArr.length % 2);
  const queryParams = [
    ...(payload.query?.where?.map((el) => where(...el)) || []),
    ...(payload.query?.orderBy ? [orderBy(payload.query?.orderBy)] : []),
    ...(payload.query?.limit ? [limit(payload.query?.limit)] : []),
  ];
  const querySnap = isCollection
    ? await getDocs(
        payload.query
          ? query(collection(db, path, ...pathSegments), ...queryParams)
          : collection(db, path, ...pathSegments)
      )
    : [await getDoc(doc(db, path, ...pathSegments))];
  querySnap.forEach((doc) => {
    doc.exists() && onEach && onEach(doc);
    doc.exists() && (snap[doc.id] = { id: doc.id, ...doc.data() });
  });
  return {
    [pathArr[isCollection ? pathArr.length - 1 : pathArr.length - 2]]: snap,
  };
};

const getFullSnapCollection = async (
  payload: SnapPayload
): Promise<SnapCollection> => {
  const fullCollectionPathArr = payload.path.split("*");
  const rootPathArr = fullCollectionPathArr[0].split(".");
  const rootProp = rootPathArr
    .slice(rootPathArr.length % 2 ? -1 : -2)
    .join(".");
  let dataPathTemp: Array<string> = [fullCollectionPathArr[0]];
  let pathArrTemp: Array<string> = [];
  const data: SnapCollection = {};
  for (let i = 0; i < fullCollectionPathArr.length; i++) {
    const newTemp: Array<string> = [];
    let newPathArrTemp: Array<string> = [];
    for (let j = 0; j < dataPathTemp.length; j++) {
      const snapPath = dataPathTemp[j];
      const isCollection = Boolean(snapPath.split(".").length % 2);
      newPathArrTemp = snapPath
        .replace(fullCollectionPathArr[0], rootProp)
        .split(".")
        .filter(Boolean);
      const deepData = pathArrTemp.reduce(
        (acc, prop) => (acc[prop] = acc[prop] || {}) && acc[prop],
        data
      );
      const nextCollectionPath = fullCollectionPathArr[i + 1];
      const snap = await getSnapCollection(
        { path: snapPath, query: payload.query },
        (doc) => {
          if (nextCollectionPath) {
            newTemp.push(
              `${snapPath}.${
                isCollection ? doc.id + "." : ""
              }${nextCollectionPath}`
            );
          }
        }
      );
      const snapKeys = Object.keys(snap);
      deepData[snapKeys[0]] = {
        ...snap[snapKeys[0]],
      };
    }
    pathArrTemp = newPathArrTemp;
    dataPathTemp = newTemp;
  }
  return data;
};

const set = async (payload: SetPayload) => {
  const pathArr = payload.path.split(".");
  const [path, ...pathSegments] = pathArr;
  const isCollection = Boolean(pathArr.length % 2);
  const ref = isCollection
    ? doc(collection(db, path, ...pathSegments))
    : doc(db, path, ...pathSegments);
  return setDoc(ref, payload.data);
};

const call = async (payload: CallPayload) => {
  const func = httpsCallable(functions, payload.function);
  return func(payload.data);
};

const change = async (payload: ChangePayload) => {
  const pathArr = payload.path.split(".");
  const [path, ...pathSegments] = pathArr;
  const isCollection = Boolean(pathArr.length % 2);
  const ref = isCollection
    ? doc(collection(db, path, ...pathSegments))
    : doc(db, path, ...pathSegments);
  return setDoc(ref, payload.data, { merge: true });
};

const remove = async (payload: RemovePayload) => {
  const pathArr = payload.path.split(".");
  const [path, ...pathSegments] = pathArr;
  const isCollection = Boolean(pathArr.length % 2);
  if (isCollection) throw new Error("Only doc");
  return deleteDoc(doc(db, path, ...pathSegments));
};

function getDocCollectionByPath(path: string) {
  return path
    .split("*")[0]
    .split(".")
    .slice(path.split("*")[0].split(".").length % 2 ? -1 : -2)[0];
}

export const firebaseApi = createApi({
  reducerPath: "firebaseApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/" }),
  tagTypes: ["Snaps"],
  endpoints: (builder) => ({
    getSnapCollection: builder.query<SnapCollection, SnapPayload>({
      async queryFn(payload) {
        const fullCollectionPathArr = payload.path.split("*");
        const data =
          fullCollectionPathArr.length > 1
            ? await getFullSnapCollection(payload)
            : await getSnapCollection(payload);

        return { data };
      },
      providesTags: (result, error, payload) => {
        const collection = getDocCollectionByPath(payload.path);
        if (result) {
          return [
            ...Object.keys(result[collection]).map((id) => ({
              type: "Snaps" as const,
              id: `${collection}${id}`,
            })),
            { type: "Snaps", id: collection },
          ];
        } else return [{ type: "Snaps", id: collection }];
      },
    }),
    addSnap: builder.mutation<any, SetPayload | CallPayload | ChangePayload>({
      async queryFn(payload) {
        if (payload.type === "call") {
          return { data: await call(payload) };
        } else if (payload.type === "set") {
          return { data: await set(payload) };
        } else {
          return { data: await change(payload) };
        }
      },
      invalidatesTags: (result, error, payload) => {
        const collection = getDocCollectionByPath(payload.path);
        return [{ type: "Snaps", id: collection }];
      },
    }),
    removeSnap: builder.mutation<any, RemovePayload>({
      async queryFn(payload) {
        return { data: await remove(payload) };
      },
      invalidatesTags: (result, error, payload) => {
        const collection = getDocCollectionByPath(payload.path);
        return [{ type: "Snaps", id: collection }];
      },
    }),
    // subscribeSnapCollection: builder.query<SnapCollection, SnapPayload>({
    //   async queryFn(payload) {
    //     const pathArr = payload.path.split(".");
    //     const data = {};
    //     (pathArr.length % 2 ? pathArr.slice(-1) : pathArr.slice(-2)).reduce(
    //       (acc, prop) => (acc[prop] = acc[prop] || {}) && acc[prop],
    //       data as SnapCollection
    //     );
    //     return { data };
    //   },
    //   async onCacheEntryAdded(
    //     payload,
    //     { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
    //   ) {
    //     let unsubscribe;
    //     try {
    //       // wait for the initial query to resolve before proceeding
    //       await cacheDataLoaded;
    //       const pathArr = payload.path.split(".");
    //       const [path, ...pathSegments] = pathArr;
    //       const isCollection = Boolean(pathArr.length % 2);
    //       const queryParams = [
    //         ...(payload.query?.where?.map((el) => where(...el)) || []),
    //         ...(payload.query?.orderBy
    //           ? [orderBy(payload.query?.orderBy)]
    //           : []),
    //         ...(payload.query?.limit ? [limit(payload.query?.limit)] : []),
    //       ];
    //       const ref = (isCollection
    //         ? payload.query
    //           ? query(collection(db, path, ...pathSegments), ...queryParams)
    //           : collection(db, path, ...pathSegments)
    //         : doc(db, path, ...pathSegments)) as any;
    //       unsubscribe = onSnapshot(ref, (snapshot: any) => {

    //       });
    //       const listener = () => {
    //         updateCachedData((draft) => {
    //           const data = (
    //             pathArr.length % 2 ? pathArr.slice(-1) : pathArr.slice(-2)
    //           ).reduce((acc, prop) => acc[prop], draft as SnapCollection);
    //           console.log(JSON.parse(JSON.stringify(draft)));
    //         });
    //       };
    //       listener();
    //     } catch {
    //       // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
    //       // in which case `cacheDataLoaded` will throw
    //     }
    //     // cacheEntryRemoved will resolve when the cache subscription is no longer active
    //     await cacheEntryRemoved;
    //     unsubscribe?.();
    //   },
    //   providesTags: (result, error, payload) =>
    //     result
    //       ? [
    //           ...Object.keys(
    //             result[
    //               payload.path
    //                 .split("*")[0]
    //                 .split(".")
    //                 .slice(
    //                   payload.path.split("*")[0].split(".").length % 2 ? -1 : -2
    //                 )[0]
    //             ]
    //           ).map((id) => ({ type: "Snaps" as const, id })),
    //           { type: "Snaps", id: "Collection" },
    //         ]
    //       : [{ type: "Snaps", id: "Collection" }],
    // }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const checkStatusBookingOption = (
  dataFlight: Snap,
  flight: string,
  cb: (a: ChangePayload) => void
) => {
  const diff = Date.now() - new Date(`${dataFlight?.createdAt}`).getTime();
  const msInMinute = 60000;
  const delayBeforeUpdateMs = msInMinute * DELAY_BOOKING_OPTION;
  if (
    diff > delayBeforeUpdateMs &&
    dataFlight?.statusSearchPrice === EStatusSearchPrice.pending
  ) {
    cb({
      type: "change",
      path: `${ECollectionName.flights}.${flight}`,
      data: { statusSearchPrice: EStatusSearchPrice.success },
    });
  }
};

export const {
  useGetSnapCollectionQuery,
  useAddSnapMutation,
  useRemoveSnapMutation,
  useLazyGetSnapCollectionQuery,
} = firebaseApi;
